import type { FormSchemaResolved } from '#layers/block-engine';
import type { AuthProviderDefinition } from '../../types/auth-providers/define';
import { BuiltinBlockIds } from 'virtual:monospace-builtin-block-ids';
import AuthProviderDisplayNameField from '#layers/access/components/Auth/AuthProviderDisplayNameField.vue';

function buildApiNameField(def: AuthProviderDefinition<any, any, any>) {
	const isPreset = Boolean(def.extends);
	return {
		name: 'apiName',
		label: '$t:auth.providers.new.api_name_label',
		description: '$t:auth.providers.new.api_name_hint',
		block: BuiltinBlockIds.Input,
		required: true,
		props: { placeholder: '$t:auth.providers.new.api_name_placeholder' },
		// Presets default to their id (kebab-safe per `ApiSafeString`); custom
		// oauth2 has none so admin must pick.
		...(isPreset ? { default: () => def.id } : {}),
	};
}

// Composite "Display Name" field — IconPicker + displayName input in one row.
// The render component injects `state` because the schema engine binds a
// single key per field but this composite writes to three.
function buildDisplayNameField() {
	return {
		name: 'displayName',
		label: '$t:auth.providers.new.display_name_label',
		description: '$t:auth.providers.new.display_name_hint',
		render: AuthProviderDisplayNameField,
	};
}

/**
 * Prepend (custom) or append (preset) the shared identity group.
 *
 * - Custom OAuth 2: top-of-form Identity + apiName — admin picks deliberately.
 * - Preset (Google, …): bottom "Advanced settings" group, apiName pre-filled.
 *
 * Preset `extends` references stay pointed at the unwrapped base, so identity
 * doesn't double-inject through `context.base.resolvedOptions`. Protocols
 * without an admin form (e.g. `password`) pass through unchanged.
 */
export function prependIdentityFields<T extends AuthProviderDefinition<any, any, any>>(def: T): T {
	if (!def.options) return def;

	const userOptions = def.options;
	const isPreset = Boolean(def.extends);
	const identityFields = [buildApiNameField(def), buildDisplayNameField()];

	const topSection: FormSchemaResolved<any> = isPreset
		? []
		: [
				{ type: 'separator', label: '$t:auth.providers.new.identity_section' },
				...identityFields,
			];

	const bottomSection: FormSchemaResolved<any> = isPreset
		? [{
				type: 'group',
				label: '$t:auth.providers.new.advanced_section',
				defaultOpen: false,
				collapsible: true,
				fields: identityFields,
			}]
		: [];

	const wrapped = (payload: any) => {
		const resolved = typeof userOptions === 'function'
			? (userOptions as (p: any) => FormSchemaResolved<any>)(payload)
			: userOptions;
		return [...topSection, ...resolved, ...bottomSection];
	};

	return { ...def, options: wrapped } as T;
}
